import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Info from 'components/shared/Info'
import { FormattedMessage } from 'react-intl'
import Price from 'components/shared/Price'
import { SummaryListWrapper } from './styles'
import SeparatedList from 'components/shared/SeparatedList'
import Quantity from 'components/global/Quantity'
import { formatPrice } from 'helpers/helpers'
import { FAMILY_PACKAGE_OPERATING_FEE, OPERATING_FEE } from 'constants/prices'
import { useAppSelector } from 'store/hooks'
import { useSelfcare } from 'store/selfcare-slice/hooks'
import Loader from 'components/shared/Loader'
import Link from 'components/shared/Link'
import { useResolutions } from 'store/resolutions'
import { useSelfcareCart } from 'store/selfcare-cart-slice/hooks'
import { SUNTAGO_ONLINE_TICKET_SALES_REGULATIONS_LINK_CHANGES_PAGE } from 'constants/links'

const SelfcareSummary = () => {
    const { ticketsBundles, isMarchPromoMode } = useAppSelector(
        (state) => state.selfcare
    )
    const {
        countChanges,
        countNewItems,
        refundPrice,
        totalPrice,
        payByCardPrice,
    } = useAppSelector((state) => state.selfcareCart)

    const { isSelfcareSummaryFetching } = useSelfcare()
    const { isSelfcareCartFetching } = useSelfcareCart()
    const { isTablet } = useResolutions()

    const isUpdating = isSelfcareCartFetching || isSelfcareSummaryFetching
    const isFamilyPackageInOrder = ticketsBundles?.length

    const orderSumPrice = (totalPrice ?? 0) - (payByCardPrice ?? 0)

    return (
        <SummaryListWrapper
            direction="column"
            gap={16}
            pt={[0, 16]}
            pb={[0, 16]}
        >
            <Text variant="heading4" color="grayscale_900" pl={[0, 16]}>
                <FormattedMessage
                    id="selfcare.summary.section_caption"
                    defaultMessage="Twoje zmiany"
                />
            </Text>
            {isMarchPromoMode ? (
                <SeparatedList
                    direction="column"
                    gap={24}
                    borderWidth={2}
                    isDashed
                >
                    <Box align="stretch" gap={24}>
                        <Box
                            direction="row"
                            align="flex-end"
                            justify="space-between"
                        >
                            <Text variant="body" color="grayscale_900" mb={4}>
                                <FormattedMessage
                                    id="global.summary.to_pay"
                                    defaultMessage="Do zapłaty"
                                />
                                :
                            </Text>

                            {isUpdating ? (
                                <Loader />
                            ) : (
                                <Price
                                    value={orderSumPrice || 0}
                                    size={isTablet ? 'heading1' : 'heading2'}
                                    color="grayscale_900"
                                />
                            )}
                        </Box>
                    </Box>
                </SeparatedList>
            ) : (
                <SeparatedList
                    direction="column"
                    gap={24}
                    borderWidth={2}
                    isDashed
                >
                    <Text variant="bodyExtraSmall" color="grayscale_700">
                        {isFamilyPackageInOrder ? (
                            <FormattedMessage
                                id="package.family.info_change_fees"
                                defaultMessage="Anulowanie lub zmiana w każdej usłudze wiąże się z opłatą operacyjną ({family_price} w przypadku Pakietu Family oraz {price} w przypadku pozostałych zmian). Opłata nie dotyczy dodawania kolejnych usług. "
                                values={{
                                    family_price: formatPrice(
                                        FAMILY_PACKAGE_OPERATING_FEE,
                                        true
                                    ),
                                    price: formatPrice(OPERATING_FEE, true),
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="selfcare.summary.info_change_fees"
                                defaultMessage="Anulowanie lub zmiana w każdej usłudze wiąże się z opłatą operacyjną ({price}). Opłata nie dotyczy dodawania kolejnych usług."
                                values={{
                                    price: formatPrice(OPERATING_FEE, true),
                                }}
                            />
                        )}{' '}
                        <Link
                            variant="secondary"
                            target="_blank"
                            icon={null}
                            path={
                                SUNTAGO_ONLINE_TICKET_SALES_REGULATIONS_LINK_CHANGES_PAGE
                            }
                            data-testid="selfcare-cart-redirect-regulations"
                        >
                            <Text variant="bodyExtraSmall" as="span">
                                <FormattedMessage
                                    id="global.regulations"
                                    defaultMessage="Regulamin"
                                />
                            </Text>
                        </Link>
                    </Text>

                    <Box gap={16} isFullWidth={true}>
                        {countChanges > 0 && (
                            <Box
                                direction="row"
                                align="center"
                                justify="space-between"
                                isFullWidth
                            >
                                <Text
                                    variant="bodyExtraSmall"
                                    color="secondary_500"
                                >
                                    <FormattedMessage
                                        id="selfcare.summary.number_of_changes"
                                        defaultMessage="Liczba zmian"
                                    />
                                </Text>
                                {isUpdating ? (
                                    <Loader />
                                ) : (
                                    <Quantity
                                        value={countChanges}
                                        size="small"
                                    />
                                )}
                            </Box>
                        )}

                        {countNewItems > 0 && (
                            <Box
                                direction="row"
                                align="center"
                                justify="space-between"
                                isFullWidth
                            >
                                <Text
                                    variant="bodyExtraSmall"
                                    color="secondary_500"
                                >
                                    <FormattedMessage
                                        id="selfcare.summary.number_of_new_items"
                                        defaultMessage="Dodane usługi"
                                    />
                                </Text>
                                {isUpdating ? (
                                    <Loader />
                                ) : (
                                    <Quantity
                                        value={countNewItems}
                                        size="small"
                                    />
                                )}
                            </Box>
                        )}
                    </Box>

                    <Box align="stretch" gap={24}>
                        <Box
                            direction="row"
                            align="flex-end"
                            justify="space-between"
                        >
                            <Text variant="body" color="grayscale_900" mb={4}>
                                <FormattedMessage
                                    id="global.summary.to_pay"
                                    defaultMessage="Do zapłaty"
                                />
                                :
                            </Text>

                            {isUpdating ? (
                                <Loader />
                            ) : (
                                <Price
                                    value={orderSumPrice || 0}
                                    size={isTablet ? 'heading1' : 'heading2'}
                                    color="grayscale_900"
                                />
                            )}
                        </Box>
                        {!!refundPrice && (
                            <Box
                                direction="row"
                                align="flex-end"
                                justify="space-between"
                            >
                                <Text
                                    variant="body"
                                    color="grayscale_900"
                                    mb={4}
                                >
                                    <FormattedMessage
                                        id="global.price.return"
                                        defaultMessage="Zwrot"
                                    />
                                    :
                                </Text>

                                {isUpdating ? (
                                    <Loader />
                                ) : (
                                    <Price
                                        value={refundPrice}
                                        size={
                                            isTablet ? 'heading1' : 'heading2'
                                        }
                                        color="grayscale_900"
                                    />
                                )}
                            </Box>
                        )}

                        <Info color="info" hasSmallFont={true} hasIcon={false}>
                            <FormattedMessage
                                id="selfcare.summary.info_voucher"
                                defaultMessage="Ewentualną różnicę w cenie zwrócimy na voucher prezentowy."
                            />
                        </Info>
                    </Box>
                </SeparatedList>
            )}
        </SummaryListWrapper>
    )
}

export default SelfcareSummary
